import { useEffect, useState } from 'react';
import { Avatar, RankMedal } from '@/art';
import { socialApi, type UserPeek, type FriendshipStatus } from '@/social/socialApi';
import { useRoomStore } from '@/stores/roomStore';
import { roomApi } from '@/rooms/roomApi';
import { useT } from '@/i18n/useI18n';

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

interface UserQuickMenuProps {
  userId: string;
  onClose: () => void;
  /** Open a DM thread with this user in the chat dock. */
  onOpenDm: (userId: string) => void;
}

export function UserQuickMenu({ userId, onClose, onOpenDm }: UserQuickMenuProps) {
  const t = useT();
  // Seed from cache so the avatar + stats paint instantly on reopen; the effect
  // below revalidates in the background.
  const [data, setData] = useState<UserPeek | null>(() => socialApi.peekCached(userId));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [invited, setInvited] = useState(false);
  const room = useRoomStore((s) => s.room);

  // Can I invite this user to my current room?
  const targetInRoom = room?.slots.some((s) => s.user?.id === userId) ?? false;
  const hasOpenSlot = room?.slots.some((s) => !s.user) ?? false;
  const canInvite = !!room
    && room.status === 'lobby'
    && hasOpenSlot
    && !targetInRoom
    && data?.friendshipStatus !== 'self';

  async function handleInviteToRoom() {
    if (!room) return;
    setBusy(true);
    try {
      await roomApi.invite(room.code, userId);
      setInvited(true);
      setToast(t('qm.invited_toast', { code: room.code }));
      window.setTimeout(() => setToast(null), 1800);
    } catch (e: any) {
      setErr(e?.message ?? t('qm.invite_failed'));
    }
    setBusy(false);
  }

  useEffect(() => {
    let cancel = false;
    socialApi.peek(userId)
      .then((d) => { if (!cancel) setData(d); })
      .catch((e) => { if (!cancel) setErr(e?.message ?? t('qm.profile_load_failed')); });
    return () => { cancel = true; };
  }, [userId]);

  async function handleAdd() {
    setBusy(true);
    try {
      const r = await socialApi.requestById(userId);
      // Refresh status
      const peek = await socialApi.peek(userId);
      setData(peek);
      setToast(r.accepted ? t('chat.now_friends') : t('chat.request_sent'));
      window.setTimeout(() => setToast(null), 1600);
    } catch (e: any) {
      setErr(e?.message ?? t('chat.send_failed'));
    }
    setBusy(false);
  }

  async function handleAccept() {
    setBusy(true);
    try {
      await socialApi.accept(userId);
      const peek = await socialApi.peek(userId);
      setData(peek);
      setToast(t('qm.accepted'));
      window.setTimeout(() => setToast(null), 1600);
    } catch (e: any) {
      setErr(e?.message ?? t('common.error'));
    }
    setBusy(false);
  }

  async function handleRemove() {
    setBusy(true);
    try {
      await socialApi.remove(userId);
      const peek = await socialApi.peek(userId);
      setData(peek);
      setToast(t('qm.unfriended'));
      window.setTimeout(() => setToast(null), 1400);
    } catch (e: any) {
      setErr(e?.message ?? t('common.error'));
    }
    setBusy(false);
  }

  async function handleToggleBlock() {
    if (!data) return;
    setBusy(true);
    try {
      if (data.blocked) await socialApi.unblock(userId);
      else await socialApi.block(userId);
      const peek = await socialApi.peek(userId);
      setData(peek);
      setToast(peek.blocked ? t('qm.blocked_toast') : t('qm.unblocked_toast'));
      window.setTimeout(() => setToast(null), 1600);
    } catch (e: any) {
      setErr(e?.message ?? t('common.error'));
    }
    setBusy(false);
  }

  async function handleReport() {
    if (!window.confirm(t('qm.report_confirm'))) return;
    setBusy(true);
    try {
      await socialApi.report(userId, 'inappropriate_behavior');
      setToast(t('qm.reported_toast'));
      window.setTimeout(() => setToast(null), 1800);
    } catch (e: any) {
      setErr(e?.message ?? t('common.error'));
    }
    setBusy(false);
  }

  return (
    <div
      className="responsive-modal"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(6px)',
        zIndex: 400,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card responsive-modal__card"
        style={{
          width: '100%',
          maxWidth: 320,
          padding: 0,
          overflow: 'hidden',
          background: 'rgba(15,18,32,0.98)',
        }}
      >
        {!data && !err && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--ink-dim)', fontSize: 12 }}>
            {t('common.loading')}
          </div>
        )}
        {err && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--c-red)', fontSize: 12 }}>
            {err}
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-ghost" onClick={onClose}>{t('common.close')}</button>
            </div>
          </div>
        )}
        {data && (
          <>
            {/* Header — avatar + name + online */}
            <div
              style={{
                padding: '20px 18px 16px',
                background: 'linear-gradient(180deg, rgba(34,211,238,0.12), transparent)',
                borderBottom: '1px solid var(--hairline)',
                display: 'flex',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <div style={{ position: 'relative' }}>
                <Avatar color={data.user.avatarColor} size={56} photoUrl={data.user.avatarUrl ?? undefined} characterUrl={data.user.characterIdleUrl} frame={data.user.frame} />
                <span
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 14,
                    height: 14,
                    borderRadius: 7,
                    background: data.user.online ? 'var(--c-cyan)' : 'var(--ink-dim)',
                    border: '2px solid var(--bg-deep)',
                    boxShadow: data.user.online ? '0 0 6px var(--c-cyan)' : 'none',
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="display" style={{ fontSize: 16, letterSpacing: '0.04em' }}>
                  {data.user.displayName}
                </div>
                <div style={{ fontSize: 10, color: data.user.online ? 'var(--c-cyan)' : 'var(--ink-dim)', marginTop: 2 }}>
                  {data.user.online ? `● ${t('common.online')}` : `○ ${t('common.offline')}`}
                </div>
              </div>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--hairline)',
                  borderRadius: 6,
                  color: 'var(--ink-muted)',
                  width: 28,
                  height: 28,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                ×
              </button>
            </div>

            {/* Stats */}
            <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <RankMedal rank={data.user.rank} size={48} />
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <StatPill label={t('qm.rank')} value={`${data.user.rank.toUpperCase()} ${DIVISION_ROMAN[data.user.rankDivision] ?? ''}`} />
                <StatPill label={t('qm.mmr')} value={String(data.user.mmr)} color="var(--c-cyan)" />
                <StatPill label={t('qm.matches')} value={String(data.user.matchesPlayed)} />
                <StatPill
                  label={t('qm.win_rate')}
                  value={data.user.matchesPlayed > 0
                    ? `${Math.round((data.user.matchesWon / data.user.matchesPlayed) * 100)}%`
                    : '—'}
                  color="var(--c-yellow)"
                />
              </div>
            </div>

            {toast && (
              <div
                style={{
                  margin: '0 18px 8px',
                  padding: '8px 12px',
                  borderRadius: 8,
                  background: 'rgba(34,211,238,0.15)',
                  border: '1px solid var(--c-cyan)',
                  color: 'var(--c-cyan)',
                  fontSize: 11,
                  textAlign: 'center',
                }}
              >
                ✓ {toast}
              </div>
            )}

            {/* Actions */}
            <div style={{ padding: '0 14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {canInvite && (
                <button
                  disabled={busy || invited}
                  onClick={handleInviteToRoom}
                  style={{
                    padding: 12,
                    fontSize: 12,
                    background: invited ? 'var(--ink-dim)' : 'rgba(155,93,229,0.18)',
                    border: `1px solid ${invited ? 'var(--ink-muted)' : 'var(--c-purple)'}`,
                    color: invited ? 'var(--ink)' : 'var(--c-purple)',
                    fontFamily: 'var(--f-display)',
                    letterSpacing: '0.06em',
                    borderRadius: 8,
                    cursor: invited ? 'default' : 'pointer',
                  }}
                >
                  {invited ? t('qm.invited_to_room', { code: room!.code }) : t('qm.invite_to_room', { code: room!.code })}
                </button>
              )}
              <FriendshipAction
                status={data.friendshipStatus}
                busy={busy}
                onAdd={handleAdd}
                onAccept={handleAccept}
                onRemove={handleRemove}
              />
              {data.friendshipStatus !== 'self' && !data.blocked && (
                <button
                  className="btn btn-primary"
                  style={{ padding: 12, fontSize: 12 }}
                  onClick={() => { onOpenDm(userId); onClose(); }}
                >
                  {t('qm.send_dm')}
                </button>
              )}
              {/* Safety actions — Block + Report (App Store Guideline 1.2) */}
              {data.friendshipStatus !== 'self' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    disabled={busy}
                    onClick={handleToggleBlock}
                    style={{
                      flex: 1,
                      padding: 10,
                      fontSize: 11,
                      background: 'transparent',
                      border: '1px solid var(--hairline)',
                      color: data.blocked ? 'var(--c-cyan)' : 'var(--ink-muted)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: 'var(--f-display)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {data.blocked ? t('qm.unblock') : t('qm.block')}
                  </button>
                  <button
                    disabled={busy}
                    onClick={handleReport}
                    style={{
                      flex: 1,
                      padding: 10,
                      fontSize: 11,
                      background: 'transparent',
                      border: '1px solid rgba(230,57,70,0.3)',
                      color: 'var(--c-red)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: 'var(--f-display)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {t('qm.report')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div
      style={{
        background: 'rgba(0,0,0,0.35)',
        border: '1px solid var(--hairline)',
        borderRadius: 8,
        padding: '6px 8px',
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 8, color: 'var(--ink-dim)', letterSpacing: '0.1em', fontFamily: 'var(--f-display)' }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: 12, color: color ?? 'var(--ink)', marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function FriendshipAction(props: {
  status: FriendshipStatus;
  busy: boolean;
  onAdd: () => void;
  onAccept: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  return <FriendshipActionInner {...props} t={t} />;
}
function FriendshipActionInner({
  status, busy, onAdd, onAccept, onRemove, t,
}: {
  status: FriendshipStatus;
  busy: boolean;
  onAdd: () => void;
  onAccept: () => void;
  onRemove: () => void;
  t: (k: any, v?: Record<string, string | number>) => string;
}) {
  if (status === 'self') return null;
  if (status === 'accepted') {
    return (
      <button
        className="btn btn-ghost"
        disabled={busy}
        onClick={onRemove}
        style={{ padding: 10, fontSize: 11, color: 'var(--c-red)', borderColor: 'rgba(230,57,70,0.3)' }}
      >
        {t('qm.friends_unfriend')}
      </button>
    );
  }
  if (status === 'pending-out') {
    return (
      <button className="btn btn-ghost" disabled style={{ padding: 10, fontSize: 11, color: 'var(--ink-dim)' }}>
        {t('qm.request_sent')}
      </button>
    );
  }
  if (status === 'pending-in') {
    return (
      <button
        className="btn"
        disabled={busy}
        onClick={onAccept}
        style={{
          padding: 12,
          fontSize: 12,
          background: 'rgba(34,211,238,0.18)',
          border: '1px solid var(--c-cyan)',
          color: 'var(--c-cyan)',
        }}
      >
        {t('qm.accept_request')}
      </button>
    );
  }
  return (
    <button
      className="btn"
      disabled={busy}
      onClick={onAdd}
      style={{
        padding: 12,
        fontSize: 12,
        background: 'rgba(244,199,36,0.18)',
        border: '1px solid var(--c-yellow)',
        color: 'var(--c-yellow)',
      }}
    >
      {t('qm.add_friend')}
    </button>
  );
}
